import {
  createAsyncThunk,
  createSlice,
  type AsyncThunk,
  type Reducer,
} from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import type { ApiError, LoadingStatus } from "@/shared/types";

/**
 * Reusable widget foundation (FR-A10, plan Phase 11 — D11): the fetch
 * lifecycle every simple data widget needs, written exactly once.
 *
 * `createDataWidget({ name, fetcher })` returns the Redux trio a widget used
 * to hand-write (thunk + slice + selectors, convention sections 26–28) with
 * the uniform state shape `{ points, status, error }` (section 49 — a failed
 * request is an error, never empty data). Widgets needing extra UI state
 * (sort, drill level, paging) compose the factory with a widget-specific
 * slice instead of extending it.
 */

/** Uniform data-widget Redux state (D2: data/loading/error live in the widget slice). */
export interface DataWidgetState<TPoint> {
  points: TPoint[];
  status: LoadingStatus;
  error: ApiError | null;
}

/** Async body of the widget's fetch: params in, rows out. */
export type DataWidgetFetcher<TPoint, TParams> = (
  params: TParams,
  signal: AbortSignal,
) => Promise<TPoint[]>;

/** Maps a rejected thunk's error into the widget's `ApiError` state. */
export function thunkError(message: string | undefined): ApiError {
  return { code: "THUNK_ERROR", message: message ?? "Unknown error" };
}

export interface DataWidget<TPoint, TParams = void> {
  name: string;
  /** Dispatch to load (or reload) the widget's points. */
  fetch: AsyncThunk<TPoint[], TParams, { state: RootState }>;
  /** Reducer for `rootReducer.ts`, registered under the widget's `name` key. */
  reducer: Reducer<DataWidgetState<TPoint>>;
  selectors: {
    selectPoints: (state: RootState) => TPoint[];
    selectLoading: (state: RootState) => boolean;
    selectError: (state: RootState) => ApiError | null;
    /** Full lifecycle state — headless widgets (e.g. fetch-once reference
     *  data) guard their "load at most once" effect on it. */
    selectStatus: (state: RootState) => LoadingStatus;
  };
}

export function createDataWidget<TPoint, TParams = void>(options: {
  /** Slice/state key — unique across the app (NFR9 naming). */
  name: string;
  fetcher: DataWidgetFetcher<TPoint, TParams>;
}): DataWidget<TPoint, TParams> {
  const { name, fetcher } = options;

  const fetch = createAsyncThunk<TPoint[], TParams, { state: RootState }>(
    `${name}/fetch`,
    (params, { signal }) => fetcher(params, signal),
  );

  const initialState: DataWidgetState<TPoint> = {
    points: [],
    status: "idle",
    error: null,
  };

  const slice = createSlice({
    name,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetch.pending, (state) => {
          state.status = "loading";
          state.error = null;
        })
        .addCase(fetch.fulfilled, (state, action) => {
          state.status = "success";
          // Immer wraps the generic row type in `Draft`; bridge back to TPoint[].
          state.points = action.payload as unknown as typeof state.points;
        })
        .addCase(fetch.rejected, (state, action) => {
          // Aborted requests are superseded fetches, not failures (section 49).
          if (action.meta.aborted) {
            return;
          }
          state.status = "error";
          state.error = thunkError(action.error.message);
        });
    },
  });

  // The reducer is registered under `name` in rootReducer.ts; the cast keeps
  // the dynamic state key contained at the factory boundary.
  const selectState = (state: RootState) =>
    (state as unknown as Record<string, DataWidgetState<TPoint>>)[name];

  return {
    name,
    fetch,
    reducer: slice.reducer,
    selectors: {
      selectPoints: (state) => selectState(state).points,
      selectLoading: (state) => selectState(state).status === "loading",
      selectError: (state) => selectState(state).error,
      selectStatus: (state) => selectState(state).status,
    },
  };
}
